<template>
  <!--
    Kontener główny sekcji filtrowania danych pojazdu:
    - .vehicle-data: dedykowana klasa dla stylów
    - d-flex, flex-wrap, flex-fill: klasy Flexbox do układu
  -->
  <div class="vehicle-data ds-panel">
    <!-- Nagłówek panelu: przetłumaczony klucz "vehicle-data", w kapitalikach -->
    <div class="ds-panel__head">
      <h3 class="ds-panel__title">{{ t('vehicle-data').toUpperCase() }}</h3>
    </div>

    <!-- Kontener dla dwóch kolumn filtrów pojazdu -->
    <div class="ds-panel__body vehicle-container">
      <!-- Lewa kolumna filtrów pojazdu -->
      <div class="vehicle-half vehicle-half--grid">
        <!-- Pojedynczy filtr: rejestracja pojazdu -->
        <single-filter
          :name="t('registration')"
          symbol="="
          fieldName="plateLpr"
        />

        <!-- Zakresowy filtr: prędkość [km/h] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('speed') + ' [km/h]'"
          :symbolFrom="t('from')"
          fieldFromName="minSpeed"
          :symbolTo="t('to')"
          fieldToName="maxSpeed"
        />

        <!-- Zakresowy filtr: masa całkowita [kg] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('short-total-weight') + ' [kg]'"
          :symbolFrom="t('from')"
          fieldFromName="minTotalLoad"
          :symbolTo="t('to')"
          fieldToName="maxTotalLoad"
        />

        <!-- Zakresowy filtr: długość pojazdu [cm] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('length') + ' [cm]'"
          :symbolFrom="t('from')"
          fieldFromName="minVehLength"
          :symbolTo="t('to')"
          fieldToName="maxVehLength"
        />

        <!-- Zakresowy filtr: wysokość pojazdu [cm] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('height') + ' [cm]'"
          :symbolFrom="t('from')"
          fieldFromName="minVehHeight"
          :symbolTo="t('to')"
          fieldToName="maxVehHeight"
        />
      </div>

      <!-- Prawa kolumna filtrów pojazdu -->
      <div class="vehicle-half vehicle-half--grid">
        <!-- Pojedynczy filtr: współczynnik podobieństwa [%] -->
        <single-filter
          :name="t('similarity')"
          symbol=">="
          fieldName="similarity"
        />

        <!-- Zakresowy filtr: liczba osi [szt.] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('no-of-axles') + ' [szt.]'"
          :symbolFrom="t('from')"
          fieldFromName="minNumberOfAxles"
          :symbolTo="t('to')"
          fieldToName="maxNumberOfAxles"
        />

        <!-- Zakresowy filtr: przeciążenie pojedynczej osi [%] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('single-axle') + ' [%]'"
          :symbolFrom="t('from')"
          fieldFromName="minOverloadSA"
          :symbolTo="t('to')"
          fieldToName="maxOverloadSA"
        />

        <!-- Zakresowy filtr: przeciążenie wieloosiowe [%] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('multiple-axle') + ' [%]'"
          :symbolFrom="t('from')"
          fieldFromName="minOverloadMA"
          :symbolTo="t('to')"
          fieldToName="maxOverloadMA"
        />

        <!-- Zakresowy filtr: dopuszczalna masa całkowita [%] -->
        <range-filter v-if="!shouldHideLocation"
          :name="t('dmc') + ' [%]'"
          :symbolFrom="t('from')"
          fieldFromName="minOverloadTL"
          :symbolTo="t('to')"
          fieldToName="maxOverloadTL"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Import Pinia store z aktualnymi filtrami
import { useCurrentFiltersStore } from "../../stores/currentFilters";
// Import komponentów filtrujących typu zakresowego i pojedynczego
import RangeFilter from "./RangeFilter.vue";
import SingleFilter from "./SingleFilter.vue";
import { computed } from "vue";
import { isSG } from "@/config/appVersion";

export default {
  name: "VehicleData",   // Nazwa komponentu
  components: { SingleFilter, RangeFilter }, // Rejestracja używanych komponentów
  setup() {
    // Inicjalizacja instancji store dla filtrów
    const filtersStore = useCurrentFiltersStore();

    const shouldHideLocation = computed(() => isSG);

    // Zwracamy store, aby używać go w template
    return { filtersStore, shouldHideLocation };
  },
  methods: {
    /**
     * Przykładowa metoda dodająca wybrany filtr do store
     * @param {number} index - indeks wybranego elementu (tu nieużywany)
     */
    setFilter(index) {
      this.filtersStore.addItem("VehicleData", index);
    },
  },
};
</script>

<style scoped>
/* Panel „Dane pojazdu" (krok 2) — dwie kolumny pól, każda kolumna to lista
   wierszy etykieta + kontrolka. Bez sztywnych szerokości: kolumny zwijają się
   do jednej przy węższym oknie. */
.vehicle-data {
  min-width: 0;
}

/* Układ kolumn i metryki pól (.vehicle-container, .vehicle-half, .name)
   pochodzą z design-system.css — są wspólne z widokiem archiwum. */

/* Wspólna siatka kolumn dla wszystkich wierszy formularza:
   etykieta | operator | pole | operator | pole

   Wiersze (`SingleFilter`, `RangeFilter`) wpinają się w nią przez `subgrid`,
   więc kolumny operatorów mają JEDNĄ szerokość wyznaczoną przez najszerszy
   tekst w danym języku („Od" po polsku, „Până la" po rumuńsku). Dzięki temu
   szerokie pole filtra pojedynczego zaczyna się dokładnie tam, co pole „od"
   w wierszach zakresowych — w każdym tłumaczeniu, bez sztywnych szerokości.

   Modyfikator, a nie samo `.vehicle-half`: tę klasę współdzieli archiwum,
   które trzyma w kolumnie inne rodzaje wierszy (checkboxy, marka/model). */
.vehicle-half--grid {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto minmax(0, 1fr);
  /* Odstęp między kolumnami MUSI być tutaj — `subgrid` bierze go z rodzica
     i ignoruje `gap` ustawiony na samym wierszu. */
  column-gap: var(--spacing-sm);
  row-gap: 0;
  align-content: start;
}
</style>
